import type { BottomBarItemProps } from '@eliadmin/core/src/components/homepage/bottom-bar';
import type { BadgeProps } from '@eliadmin/core/src/components/ui/badge';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { BiSolidCategory } from 'react-icons/bi';
import { FaXTwitter } from 'react-icons/fa6';
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaPinterest,
  FaTiktok,
  FaUser,
  FaYoutube,
} from 'react-icons/fa';
import { FiShoppingBag, FiHome, FiAlignLeft, FiHeart, FiSettings, FiUser } from 'react-icons/fi';
import {
  RiArrowRightSLine,
  RiFacebookLine,
  RiHourglassLine,
  RiInstagramLine,
  RiLinkedinLine,
  RiMenuFill,
  RiTwitterXLine,
} from 'react-icons/ri';

import {
  LuAlarmClock,
  LuCalendarDays,
  LuCreditCard,
  LuHeart,
  LuLogOut,
  LuShoppingCart,
  LuUser,
} from 'react-icons/lu';
import type { ProfileLinkItems } from '../components/header/header-profile-button';

export const HEADER_CONFIG = {
  bottomBar: {
    items: [
      {
        key: 'home',
        href: () => ROUTES.HOME(),
        icon: <FiHome className="h-5 w-5" />,
        label: 'text160',
      },
      {
        key: 'categories',
        href: () => ROUTES.CATEGORIES(),
        icon: <BiSolidCategory className="h-5 w-5" />,
        label: 'text176',
      },
      {
        key: 'cart',
        href: () => ROUTES.CART(),
        label: 'text343',
        icon: <FiShoppingBag className="h-5 w-5" />,
      },
      {
        key: 'favorites',
        href: () => ROUTES.FAVORITES(),
        label: 'text516',
        icon: <FiHeart className="h-5 w-5" />,
      },
      {
        key: 'profile',
        href: () => ROUTES.PROFILE(),
        label: 'text486',
        icon: <FaUser className="h-5 w-5" />,
      },
    ] satisfies BottomBarItemProps[],
  },
  searchBar: {
    buttonVariant: 'default' as ButtonProps['variant'],
    inputVariant: 'outlined' as InputProps['variant'],
  },
  headerMain: {
    profile: {
      buttonVariant: 'outline' as ButtonProps['variant'],
      icon: <FiUser className="h-5 w-5" />,
    },
    favorites: {
      buttonVariant: 'outline' as ButtonProps['variant'],
      icon: <FiHeart className="h-5 w-5" />,
    },
    contact: {
      icon: <FiSettings className="h-6 w-6 fill-primary" />,
    },
    cart: {
      icon: <FiShoppingBag className="h-5 w-5 text-[#999999]" />,
      badgeVariant: 'accent' as BadgeProps['variant'],
    },
  },
  categoryButton: {
    icon: <FiAlignLeft className="h-5 w-5" />,
    text: 'text176',
  },
  sidebar: {
    triggerIcon: <RiMenuFill className="h-6 w-6" />,
    menuArrow: <RiArrowRightSLine className="h-6 w-6" />,
    menuItems: [
      {
        key: 'domov',
        title: 'Domov',
        icon: <RiHourglassLine className="h-6 w-6" />,
        href: () => ROUTES.HOME(),
      },
      {
        key: 'profil',
        title: 'Profil',
        icon: <RiHourglassLine className="h-6 w-6" />,
        href: () => ROUTES.HOME(),
      },
      {
        key: 'oblubene',
        title: 'Obľúbené',
        icon: <RiHourglassLine className="h-6 w-6" />,
        href: () => ROUTES.FAVORITES(),
      },
      {
        key: 'kosik',
        title: 'Košík',
        icon: <RiHourglassLine className="h-6 w-6" />,
        href: () => ROUTES.CART(),
      },
    ],
    ctaButtons: [
      {
        variant: 'accent',
        title: 'Call to Action',
        href: '/',
        className: '',
      },
      {
        variant: 'secondary',
        title: 'Action to Call',
        href: '/',
        className: '',
      },
    ],
    icons: [
      {
        key: 'instagram',
        icon: <RiInstagramLine className="h-6 w-6" />,
        href: 'www.instagram.com',
      },
      {
        key: 'facebook',
        icon: <RiFacebookLine className="h-6 w-6" />,
        href: 'www.facebook.com',
      },
      {
        key: 'linkedin',
        icon: <RiLinkedinLine className="h-6 w-6" />,
        href: 'www.linkedin.com',
      },
      {
        key: 'twitter',
        icon: <RiTwitterXLine className="h-6 w-6" />,
        href: 'www.twitter.com',
      },
    ],
  },
  socialMediaIcons: {
    facebook: <FaFacebook fill="#4267B2" />,
    instagram: <FaInstagram fill="#F56040" />,
    linkedin: <FaLinkedin fill="#0077B5" />,
    pinterest: <FaPinterest fill="#E60023" />,
    tiktok: <FaTiktok />,
    twitter: <FaXTwitter />,
    youtube: <FaYoutube fill="#FF0000" />,
  },
  profileLinks: [
    {
      key: 'profile',
      name: 'text1134',
      icon: <LuUser size={20} />,
      href: () => ROUTES.PROFILE(),
    },
    {
      key: 'subscriptions',
      name: 'text1917',
      icon: <LuCreditCard size={20} />,
      href: () => ROUTES.SUBSCRIPTIONS(),
    },
    {
      key: 'separator-1',
      name: 'separator',
    },
    {
      key: 'my-courses',
      name: 'text2424',
      icon: <LuCalendarDays size={20} />,
      href: () => ROUTES.MY_COURSES(),
    },
    {
      key: 'my-orders',
      name: 'text257',
      icon: <LuShoppingCart size={20} />,
      href: () => ROUTES.ORDERS(),
    },
    {
      key: 'favorites',
      name: 'text516',
      icon: <LuHeart size={20} />,
      href: () => ROUTES.FAVORITES(),
    },
    {
      key: 'separator-2',
      name: 'separator',
    },
    {
      key: 'sign-out',
      name: 'text258',
      icon: <LuLogOut size={20} />,
      href: () => ROUTES.SIGN_OUT(),
    },
  ] as ProfileLinkItems[],
} as const;
